// Force rebuild
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import LoginPage from './components/LoginPage';
import ProUpgradeModal from './components/ProUpgradeModal';
import { FEATURE_FLAGS } from './config/featureFlags';
import { useProUpgradeModal } from './hooks/useProUpgradeModal';
import { mockProgressData } from './data/mockData';
import { DarkModeProvider } from './contexts/DarkModeContext';
import { AuthProvider, useAuth } from './AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { StreakProvider } from './contexts/StreakContext';
import { CelebrationProvider } from './contexts/CelebrationContext';
import { useHeartbeat } from './hooks/useHeartbeat';
import { AdminPage } from './pages/AdminPage';
import { AdminUserDetail } from './pages/AdminUserDetail';
import './utils/resetAnnouncement';
import './App.css';
import OnboardingWizard from './components/onboarding/OnboardingWizard';
import PreAuthWizard from './components/onboarding/PreAuthWizard';
import MyPlanPage from './pages/MyPlanPage';
import QuizRecapModule from './components/QuizRecapModule';
import VerifyActionPage from './pages/VerifyActionPage';

import { useStudyPlanManager } from './hooks/useStudyPlanManager';

function AuthenticatedApp() {
  const { currentUser } = useAuth();
  const { showModal, closeModal } = useProUpgradeModal();

  // Study Plan Manager - Checks for daily rollover
  const { checkForNewDay } = useStudyPlanManager();
  const { isDevMode } = useAuth(); // Moved to top level

  React.useEffect(() => {
    if (currentUser) {
      checkForNewDay();
    }
  }, [currentUser, checkForNewDay]);

  // Enable Admin Heartbeat
  useHeartbeat();

  if (!currentUser) {
    return <LoginPage />;
  }

  // Block unverified email users, but let Google/DevMode users pass
  const isGoogleUser = currentUser.providerData?.some(
    (provider) => provider.providerId === 'google.com'
  );

  if (!currentUser.emailVerified && !isGoogleUser && !isDevMode) {
    return <LoginPage autoVerify />;
  }

  // Convert Firebase user to our user format
  const user = {
    id: currentUser.uid,
    name: currentUser.displayName || 'User',
    email: currentUser.email || '',
    avatar: currentUser.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser.displayName || 'User')}&background=3b82f6&color=fff`,
    studyGoals: {
      dailyFlashcards: 20,
      weeklyHours: 5
    }
  };

  return (
    <>
      <Dashboard user={user} progressData={mockProgressData} />
      <ProUpgradeModal isOpen={showModal} onClose={closeModal} />
    </>
  );
}

function AppContent() {
  const { currentUser, isDevMode } = useAuth();

  const isGoogleUser = currentUser?.providerData?.some(
    (provider) => provider.providerId === 'google.com'
  );

  const isVerifiedAuth = currentUser && (currentUser.emailVerified || isGoogleUser || isDevMode);

  return (
    <Router>
      <Routes>
        <Route path="/admin" element={isVerifiedAuth ? <AdminPage /> : <Navigate to="/" />} />
        <Route path="/admin/users/:uid" element={isVerifiedAuth ? <AdminUserDetail /> : <Navigate to="/" />} />
        {FEATURE_FLAGS.ONBOARDING_WIZARD && (
          <>
            <Route path="/onboarding" element={isVerifiedAuth ? <OnboardingWizard /> : <Navigate to="/" />} />
            <Route path="/plan" element={isVerifiedAuth ? <MyPlanPage /> : <Navigate to="/" />} />
            <Route path="/quiz-recap" element={isVerifiedAuth ? <QuizRecapModule /> : <Navigate to="/" />} />
          </>
        )}

        {/* Email verification action handler (replaces Firebase default page) */}
        <Route path="/verify-action" element={<VerifyActionPage />} />

        {/* Pre-auth wizard for new users */}
        <Route path="/welcome" element={<PreAuthWizard />} />

        {/* Login page for explicit navigation */}
        <Route path="/login" element={isVerifiedAuth ? <Navigate to="/home" replace /> : <LoginPage />} />

        {/* Explicit Dashboard Routes */}
        <Route path="/home" element={<AuthenticatedApp />} />
        <Route path="/osp" element={<AuthenticatedApp />} />
        <Route path="/osp" element={<AuthenticatedApp />} />
        <Route path="/textbook" element={<AuthenticatedApp />} />
        <Route path="/courses" element={<AuthenticatedApp />} />
        <Route path="/flashcards" element={<AuthenticatedApp />} />
        <Route path="/testy" element={<AuthenticatedApp />} />
        <Route path="/audiobook" element={<AuthenticatedApp />} />
        <Route path="/settings" element={<AuthenticatedApp />} />
        <Route path="/upgrade" element={<AuthenticatedApp />} />
        <Route path="/upgrade-success" element={<AuthenticatedApp />} />
        <Route path="/help" element={<AuthenticatedApp />} />
        <Route path="/feedback" element={<AuthenticatedApp />} />

        {/* Fallback - redirect root to home or onboarding */}
        <Route path="/" element={isVerifiedAuth ? <Navigate to="/home" replace /> : <LoginPage />} />
        <Route path="/*" element={<AuthenticatedApp />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <DarkModeProvider>
      <AuthProvider>
        <ToastProvider>
          <StreakProvider>
            <CelebrationProvider>
              <AppContent />
            </CelebrationProvider>
          </StreakProvider>
        </ToastProvider>
      </AuthProvider>
    </DarkModeProvider>
  );
}

export default App;